import { useEffect, useState } from 'react';
import { z } from 'zod';
import { developmentDirectionSchema, type DevelopmentDirection } from '@sqi/shared-schemas';
import { request, type OemWorkspace } from '../../lib/oem-client';
import { displayValue } from '../../lib/oem-display';
import type { OemContext } from '../../lib/auth';
export interface QualifiedBase {
  id: string;
  category: string;
  eligible: boolean;
  formula_id: string;
  formula_version: number;
  formula_hash: string;
  direction: DevelopmentDirection;
}
const sourceLines = z.array(
  z
    .object({
      materialId: z.uuid(),
      supplierSpecId: z.uuid(),
      constituents: z.array(z.object({ ingredientId: z.uuid() }).loose()),
    })
    .loose(),
);
export function DevelopmentLibrary({
  context,
  workspace,
  category,
  selectedId,
  onSelect,
  onLoad,
}: {
  context: OemContext;
  workspace: OemWorkspace;
  category: string;
  selectedId: string;
  onSelect: (id: string) => void;
  onLoad: (rows: QualifiedBase[]) => void;
}) {
  const [rows, setRows] = useState<QualifiedBase[]>([]),
    [formulaId, setFormulaId] = useState(''),
    [source, setSource] = useState(''),
    [error, setError] = useState(''),
    [busy, setBusy] = useState(false);
  const [annotations, setAnnotations] = useState<
    {
      materialId: string;
      supplierSpecId: string;
      function: string;
      phase: 'A' | 'B' | 'C';
      activeIngredientIds: string[];
    }[]
  >([]);
  const [qualifiedCategory, setQualifiedCategory] = useState('moisturizer');
  async function load() {
    const result = await request('/development/library/' + context.projectId);
    const parsed = z
      .array(
        z
          .object({
            id: z.uuid(),
            category: z.string(),
            eligible: z.boolean(),
            formula_id: z.uuid(),
            formula_version: z.int(),
            formula_hash: z.string(),
            direction: developmentDirectionSchema,
          })
          .loose(),
      )
      .parse(result);
    setRows(parsed);
    onLoad(parsed);
  }
  useEffect(() => {
    void request('/development/library/' + context.projectId)
      .then((result) => {
        const parsed = z
          .array(
            z
              .object({
                id: z.uuid(),
                category: z.string(),
                eligible: z.boolean(),
                formula_id: z.uuid(),
                formula_version: z.int(),
                formula_hash: z.string(),
                direction: developmentDirectionSchema,
              })
              .loose(),
          )
          .parse(result);
        setRows(parsed);
        onLoad(parsed);
      })
      .catch(() => setError('โหลดคลังที่คัดคุณสมบัติไม่ได้'));
  }, [context.projectId, onLoad]);
  const formula = workspace.records.find((r) => r.id === formulaId);
  const lines = sourceLines.safeParse(formula?.content.lines);
  return (
    <section className="scope">
      <h3>คลังฐานสูตรที่คัดคุณสมบัติ · เลือกก่อนสร้างแนวทาง</h3>
      <p>
        ฐานตรึง formula ID/version/hash และสเปกที่ผ่าน checker การนำเข้าเป็นร่างพัฒนาใหม่ ต้องทบทวน
        R&D/QA/ตัวอย่างอีกครั้ง ไม่มีการอนุญาตผลิตจริง
      </p>
      <label>
        ฐานที่ใช้สร้างแนวทาง
        <select value={selectedId} onChange={(e) => onSelect(e.target.value)}>
          <option value="">ใช้ฐานที่เข้าเกณฑ์แรก หรือแม่แบบทดลองถ้าไม่มี</option>
          {rows
            .filter((q) => q.category === category)
            .map((q) => (
              <option key={q.id} value={q.id} disabled={!q.eligible}>
                {q.direction.label} v{q.formula_version} · {q.formula_hash.slice(0, 12)}{' '}
                {q.eligible ? 'เข้าเกณฑ์' : 'หลักฐานไม่ปัจจุบัน'}
              </option>
            ))}
        </select>
      </label>
      <button
        className="action secondary"
        onClick={() => {
          void load().catch(() => setError('โหลดคลังไม่ได้'));
        }}
      >
        โหลดคลังล่าสุด
      </button>
      {context.role === 'rd_checker' ? (
        <>
          <h4>Checker คัดคุณสมบัติแหล่งสูตรเดิม</h4>
          <label>
            สูตรต้นทางที่ผ่านทบทวน
            <select
              value={formulaId}
              onChange={(e) => {
                const f = workspace.records.find((r) => r.id === e.target.value);
                const l = sourceLines.safeParse(f?.content.lines);
                setFormulaId(e.target.value);
                setAnnotations(
                  l.success
                    ? l.data.map((x) => ({
                        materialId: x.materialId,
                        supplierSpecId: x.supplierSpecId,
                        function: '',
                        phase: 'A',
                        activeIngredientIds: [],
                      }))
                    : [],
                );
              }}
            >
              <option value="">เลือกสูตรต้นทาง</option>
              {workspace.records
                .filter((r) => r.kind === 'formula')
                .map((r) => (
                  <option key={r.id} value={r.id}>
                    {String(r.content.label)} v{r.version} · {r.hash.slice(0, 12)}
                  </option>
                ))}
            </select>
          </label>
          <label>
            ประเภทเครื่องสำอางที่ checker ยืนยัน
            <select
              value={qualifiedCategory}
              onChange={(e) => setQualifiedCategory(e.target.value)}
            >
              {['moisturizer', 'body_wash', 'cosmetic_serum'].map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>
          {lines.success
            ? lines.data.map((l, i) => (
                <div key={i}>
                  <p>
                    {String(workspace.records.find((r) => r.id === l.materialId)?.content.label)} ·
                    spec {l.supplierSpecId}
                  </p>
                  <label>
                    หน้าที่สาร {i + 1}
                    <input
                      value={annotations[i]?.function ?? ''}
                      onChange={(e) =>
                        setAnnotations(
                          annotations.map((a, n) =>
                            n === i ? { ...a, function: e.target.value } : a,
                          ),
                        )
                      }
                    />
                  </label>
                  <label>
                    เฟส {i + 1}
                    <select
                      value={annotations[i]?.phase ?? 'A'}
                      onChange={(e) =>
                        setAnnotations(
                          annotations.map((a, n) =>
                            n === i ? { ...a, phase: e.target.value as 'A' | 'B' | 'C' } : a,
                          ),
                        )
                      }
                    >
                      {['A', 'B', 'C'].map((p) => (
                        <option key={p}>{p}</option>
                      ))}
                    </select>
                  </label>
                  <p>
                    เลือก constituent ที่เป็น active สำหรับการคำนวณ (ไม่เลือก = active fraction 0)
                  </p>
                  {l.constituents.map((c) => (
                    <label key={c.ingredientId}>
                      <input
                        type="checkbox"
                        checked={
                          annotations[i]?.activeIngredientIds.includes(c.ingredientId) ?? false
                        }
                        onChange={(e) =>
                          setAnnotations(
                            annotations.map((a, n) =>
                              n === i
                                ? {
                                    ...a,
                                    activeIngredientIds: e.target.checked
                                      ? [...a.activeIngredientIds, c.ingredientId]
                                      : a.activeIngredientIds.filter((id) => id !== c.ingredientId),
                                  }
                                : a,
                            ),
                          )
                        }
                      />
                      {displayValue(
                        workspace.records.find((r) => r.id === c.ingredientId)?.content.inci ??
                          c.ingredientId,
                      )}
                    </label>
                  ))}
                </div>
              ))
            : null}
          <label>
            หลักฐานจำแนกประเภท/ขอบเขตการคัดคุณสมบัติ
            <textarea value={source} onChange={(e) => setSource(e.target.value)} />
          </label>
          <button
            className="action"
            disabled={busy || !formula || !source || !annotations.length}
            onClick={() => {
              void (async () => {
                setBusy(true);
                setError('');
                try {
                  await request('/development/library/qualify', {
                    formulaId: formula?.id,
                    hash: formula?.hash,
                    category: qualifiedCategory,
                    annotations,
                    source,
                  });
                  await load();
                } catch (e) {
                  setError(e instanceof Error ? e.message : 'คัดคุณสมบัติไม่ได้');
                } finally {
                  setBusy(false);
                }
              })();
            }}
          >
            คัดคุณสมบัติและเก็บ snapshot ต้นทาง
          </button>
        </>
      ) : null}
      {error ? <p role="alert">{error}</p> : null}
    </section>
  );
}
